// KHLIFI HOUCEM / FORMATEUR CLOUD && DEVSECOPS
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { interviewAPI } from "@/lib/api";
import { WorkspaceLayout } from "@/components/layout/WorkspaceLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Mic, HelpCircle, Layers, Users, Play, Clock, Target,
  CheckCircle2, XCircle, AlertTriangle, Trophy, ArrowRight, Plus
} from "lucide-react";
import { toast } from "sonner";

const REC_COLORS = { rejected: "text-red-500", review: "text-yellow-500", retained: "text-green-500", excellent: "text-emerald-500" };
const STATUS_LABELS = { draft: "Brouillon", in_progress: "En cours", completed: "Terminé" };

export default function InterviewsPage() {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ questions: 0, templates: 0, candidates: 0, interviews: 0, completed: 0, avgScore: 0 });
  const [recentInterviews, setRecentInterviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadData(); }, [workspaceId]);

  const loadData = async () => {
    try {
      const [qRes, tRes, cRes, iRes] = await Promise.all([
        interviewAPI.listQuestions(workspaceId, { limit: 1 }),
        interviewAPI.listTemplates(workspaceId),
        interviewAPI.listCandidates(workspaceId),
        interviewAPI.listInterviews(workspaceId),
      ]);
      const interviews = iRes.data;
      const completed = interviews.filter(i => i.status === "completed");
      const avgScore = completed.length > 0 ? completed.reduce((a, i) => a + (i.weighted_percentage || i.percentage || 0), 0) / completed.length : 0;

      // Get total question count from a separate call
      const qFull = await interviewAPI.listQuestions(workspaceId, {});

      setStats({
        questions: qFull.data.length,
        templates: tRes.data.length,
        candidates: cRes.data.length,
        interviews: interviews.length,
        completed: completed.length,
        avgScore: Math.round(avgScore),
      });
      setRecentInterviews(interviews.slice(0, 5));
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const statCards = [
    { label: "Questions", value: stats.questions, icon: HelpCircle, color: "text-violet-500", bg: "bg-violet-100", href: "questions" },
    { label: "Templates", value: stats.templates, icon: Layers, color: "text-blue-500", bg: "bg-blue-100", href: "templates" },
    { label: "Candidats", value: stats.candidates, icon: Users, color: "text-emerald-500", bg: "bg-emerald-100", href: "candidates" },
    { label: "Entretiens", value: stats.interviews, icon: Mic, color: "text-orange-500", bg: "bg-orange-100", href: "" },
  ];

  return (
    <WorkspaceLayout>
      <div className="page-container animate-fade-in">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 shadow-sm">
                <Mic className="h-5 w-5 text-white" />
              </div>
              Entretiens
            </h1>
            <p className="mt-1 text-muted-foreground">Gérez vos entretiens techniques DevOps</p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {statCards.map((s) => (
            <Card key={s.label} className="hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => navigate(`/workspace/${workspaceId}/interviews/${s.href}`)}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div className={`flex items-center justify-center w-10 h-10 rounded-xl ${s.bg}`}>
                    <s.icon className={`h-5 w-5 ${s.color}`} />
                  </div>
                  <span className="text-3xl font-bold">{loading ? "—" : s.value}</span>
                </div>
                <p className="text-sm text-muted-foreground mt-2">{s.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card className="hover:shadow-lg transition-all cursor-pointer group border-primary/20 hover:border-primary/50"
            onClick={() => navigate(`/workspace/${workspaceId}/interviews/questions`)}>
            <CardContent className="pt-6 flex items-center gap-4">
              <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-primary/10 group-hover:bg-primary/20 transition-colors">
                <HelpCircle className="h-6 w-6 text-primary" />
              </div>
              <div className="flex-1">
                <p className="font-semibold">Banque de questions</p>
                <p className="text-sm text-muted-foreground">Créer, organiser et importer</p>
              </div>
              <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-all cursor-pointer group border-primary/20 hover:border-primary/50"
            onClick={() => navigate(`/workspace/${workspaceId}/interviews/templates`)}>
            <CardContent className="pt-6 flex items-center gap-4">
              <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-blue-100 group-hover:bg-blue-200 transition-colors">
                <Layers className="h-6 w-6 text-blue-600" />
              </div>
              <div className="flex-1">
                <p className="font-semibold">Templates d'entretien</p>
                <p className="text-sm text-muted-foreground">Structurer les évaluations</p>
              </div>
              <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-blue-600 transition-colors" />
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-all cursor-pointer group border-primary/20 hover:border-primary/50"
            onClick={() => navigate(`/workspace/${workspaceId}/interviews/candidates`)}>
            <CardContent className="pt-6 flex items-center gap-4">
              <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-emerald-100 group-hover:bg-emerald-200 transition-colors">
                <Users className="h-6 w-6 text-emerald-600" />
              </div>
              <div className="flex-1">
                <p className="font-semibold">Candidats</p>
                <p className="text-sm text-muted-foreground">Pipeline de recrutement</p>
              </div>
              <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-emerald-600 transition-colors" />
            </CardContent>
          </Card>
        </div>

        {/* Recent Interviews */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Entretiens récents</CardTitle>
              {stats.completed > 0 && (
                <div className="flex items-center gap-2">
                  <Target className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Score moyen : <span className="font-bold text-foreground">{stats.avgScore}%</span></span>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {recentInterviews.length === 0 ? (
              <div className="text-center py-8">
                <Mic className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">Aucun entretien pour l'instant</p>
                <p className="text-xs text-muted-foreground mt-1">Commencez par créer des questions et un template</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentInterviews.map((itw) => (
                  <div key={itw.id} className="flex items-center gap-4 p-3 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                    onClick={() => {
                      if (itw.status === "completed") navigate(`/workspace/${workspaceId}/interviews/summary/${itw.id}`);
                      else navigate(`/workspace/${workspaceId}/interviews/live/${itw.id}`);
                    }}>
                    <div className={`w-2 h-2 rounded-full ${itw.status === "completed" ? "bg-green-500" : itw.status === "in_progress" ? "bg-orange-500" : "bg-gray-300"}`} />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm">{itw.candidate_name}</p>
                      <p className="text-xs text-muted-foreground">{itw.template_title}</p>
                    </div>
                    <Badge variant="outline" className="text-xs">{STATUS_LABELS[itw.status] || itw.status}</Badge>
                    {itw.percentage > 0 && <span className="text-sm font-medium">{Math.round(itw.weighted_percentage || itw.percentage)}%</span>}
                    {itw.recommendation && (
                      <span className={`text-xs font-medium ${REC_COLORS[itw.recommendation_override || itw.recommendation]}`}>
                        {itw.recommendation_override || itw.recommendation}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </WorkspaceLayout>
  );
}
